import { cloneDeep as _cloneDeep } from 'lodash'
import React, { Component } from 'react'
import { Asn, DecodedItem, IdentifierType, Parcels, ProductInfoField, ShippingParcel, Shippings } from 'stylewhere/api'
import {
  AntennaButton,
  Box,
  Button,
  FullLoadingLayer,
  OperationReadingCounters,
  OperationReadingList,
  Page,
  RemoveMode,
  Spacer,
  Stoplight,
  TagCounter,
} from 'stylewhere/components'
import { BarcodeModeOptions } from 'stylewhere/components/AntennaButton'
import { config } from 'stylewhere/config'
import { ShippingExtensions } from 'stylewhere/extensions'
import { T, __, __UP } from 'stylewhere/i18n'
import { Routes } from 'stylewhere/pages'
import {
  AppStore,
  checkRequiredField,
  getDataFromSchema,
  OperationReadingProps,
  OperationReadingProvider,
  OperationReadingState,
  RemoteOperation,
  RfidReader,
  Router,
} from 'stylewhere/shared'
import { CustomError } from 'stylewhere/shared/errors'
import { ShippingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  askUserConfirmation,
  checkItemsCounterError,
  filterErrorItems,
  filterIgnoredAndErrorItems,
  filterIgnoredItems,
  getChecklistItemsCount,
  getSseEndpoint,
  hasAsyncConfirm,
  hasChecklist,
  isModalError,
  RETRY_SSE_TIME,
  showToast,
  showToastError,
  validateChecklistReadings,
} from 'stylewhere/shared/utils'

export interface ShippingState extends OperationReadingState {
  parcel?: ShippingParcel
  asn?: Asn
  confirming: boolean
  lastAsyncID: string
  sseStatus: boolean
  retrySseCount: number
  backRoute?: Routes
  backRouteParams?: Record<string, string>
  removeModeEnabled: boolean
  autostartAntenna?: boolean
  autoConfirmParcel?: boolean
  initReaderTags: string[]
  barcodeItemReading: boolean
}

export default class ShippingReading<
  P extends OperationReadingProps<ShippingState>,
  S extends ShippingState
> extends Component<P, S> {
  antennaRef
  matchParams = Router.getMatchParams(this.props)
  locationState = Router.getLocationState<ShippingState>(this.props)
  operation = RemoteOperation.getOperationConfig<ShippingOperationConfig>(this.matchParams.opCode)
  formSchema = ShippingExtensions.formSchema(this.operation, { parcel: this.locationState.parcel })
  sse: any
  isModal = false
  asn?: Asn = this.locationState.asn

  public state: S = {
    items: [],
    loading: true,
    formData: this.locationState.formData ?? {},
    confirming: false,
    lastAsyncID: '',
    sseStatus: false,
    retrySseCount: 0,
    removeModeEnabled: false,
    initReaderTags: [],
    barcodeItemReading: false,
  } as any

  constructor(props) {
    super(props)
    this.antennaRef = React.createRef()
    this.removeItem = this.removeItem.bind(this)
  }

  isBatchReadingMode = this.operation.persistParcelContentScannings === 'onBatchRead'
  operationRemoveModeAllowed = this.operation.removeMode && ['sku', 'rfid'].includes(this.operation.removeMode)
  isRemoveModeBySku = this.operation.removeMode === 'sku'
  isRemoveModeByRfidRead = this.operation.removeMode === 'rfid'

  kickBackCondition = () => {
    return !this.locationState.formData
  }

  disableFormData = () => {
    return false
  }

  canAutoStartAntenna = () => {
    return true
  }

  async componentDidMount() {
    if (this.kickBackCondition()) {
      this.goBack()
      return
    }

    const userSetAutostopAntennaTimeout: number =
      (await AppStore.getUserSetting('autostopAntennaTimeout', this.operation.id))?.value ||
      this.operation.autostopAntennaTimeout
    const userSetAutostartAntenna: boolean =
      (await AppStore.getUserSetting('autostartAntenna', this.operation.id))?.value || this.operation.autostartAntenna
    const userSetAutoConfirmParcel: boolean =
      (await AppStore.getUserSetting('autoConfirmParcel', this.operation.id))?.value || false
    const userBarcodeItemReading: boolean =
      (await AppStore.getUserSetting('barcodeItemRead', this.operation.id))?.value || false

    this.isModal = isModalError(this.operation)
    RfidReader.setBatchInterval(this.operation.batchInterval)
    RfidReader.setBatchIntervalTagCount(this.operation.batchIntervalTagCount)
    RfidReader.setBatchIntervalTime(this.operation.batchIntervalTime)
    RfidReader.setAutomaticStop(userSetAutostopAntennaTimeout > 0)
    RfidReader.setAutomaticStopTime(userSetAutostopAntennaTimeout)
    OperationReadingProvider.init(
      {
        ...this.operation,
        autostartAntenna: this.canAutoStartAntenna() && !userBarcodeItemReading && userSetAutostartAntenna,
      },
      this.locationState,
      this.goBack,
      this.setRfidReaderDecode,
      this.disableFormData()
    )

    let parcel: ShippingParcel | undefined = this.locationState.parcel
    if (this.isBatchReadingMode) {
      try {
        if (!checkRequiredField(this.state.formData, this.formSchema)) {
          await this.openRequiredFieldsModal()
        }
        const createParcelResponse = await this.createParcel()
        parcel = createParcelResponse?.parcel
        if (!parcel) throw new Error('Missing parcel')
      } catch (error) {
        showToastError(error, __(T.error.error), this.isModal)
      }
    }

    const entriesItem: DecodedItem[] = []
    let tagsToAdd: string[] = []
    if (parcel && parcel.parcelEntries && parcel.parcelEntries.length > 0) {
      parcel.parcelEntries.map((entry) => {
        if (entry.item && entry.item !== null) {
          const uhfTag = entry.item.identifiers.filter((e) => e.type === 'UHF_TAG').flatMap(({ code }) => code)
          tagsToAdd = tagsToAdd.concat(uhfTag)
          entriesItem.push({
            item: entry.item,
            statuses: [],
            identifierCode: uhfTag.length > 0 ? uhfTag[0] : '',
          })
        }
      })
    }

    this.setState(
      {
        parcel,
        items: entriesItem,
        initReaderTags: tagsToAdd,
        sseStatus: !hasAsyncConfirm(this.operation),
        autostartAntenna: userSetAutostartAntenna,
        autoConfirmParcel: userSetAutoConfirmParcel,
        barcodeItemReading: userBarcodeItemReading,
      },
      this.attachSse
    )
  }

  componentWillUnmount = () => {
    this.destroySse()
  }

  canShowConfirmButton = () => {
    const { items } = this.state
    const isItemsAvailable = items.filter(filterIgnoredItems)
    return isItemsAvailable.length > 0
  }

  confirmButtonEnabled = () => {
    return !this.state.autoConfirmParcel
  }

  parcelIsSuitableForConfirmation = () => {
    const { parcel, items } = this.state
    if (!parcel) return false
    const itemsCount = items
      .filter(filterIgnoredAndErrorItems)
      .reduce((acc, item) => acc + OperationReadingProvider.getItemIntrinsicQuantity(item, ShippingExtensions), 0)
    const { expecteds, overQty, unexpecteds } = getChecklistItemsCount(
      items,
      this.operation,
      ShippingExtensions,
      parcel.checklist,
      parcel.products
    )
    return itemsCount === expecteds && !overQty && !unexpecteds
  }

  attachSse = async () => {
    if (!hasAsyncConfirm(this.operation) || !AppStore.loggedUser) {
      return
    }
    const sseUrl = getSseEndpoint(config)
    this.sse = new EventSource(sseUrl, { withCredentials: false })
    this.sse.onerror = () => {
      this.setState({ sseStatus: false, retrySseCount: this.state.retrySseCount + 1 }, this.retrySse)
    }
    this.sse.onopen = (event) => {
      this.setState({ sseStatus: true, retrySseCount: 0 })
    }
    this.sse.onmessage = (event) => {
      this.sseEventMessage(event)
    }
  }

  sseEventMessage = async (event) => {
    const { lastAsyncID } = this.state
    const eventData = event.data ? JSON.parse(event.data) : undefined
    if (eventData && eventData.data) {
      if (eventData.data.asyncExecutionId === lastAsyncID) {
        if (eventData.data.executionStatus === 'OK') {
          try {
            const res = await Parcels.getById(eventData.data.details.operationInstanceId)
            this._confirmSuccess(res)
          } catch (error) {
            this._confirmSuccess()
          }
        } else {
          this._confirmFailed(eventData.data.details || '')
        }
      }
    }
  }

  retrySse = () => {
    this.destroySse()
    if (this.state.retrySseCount === 1) {
      this.attachSse()
    } else {
      setTimeout(() => {
        this.attachSse()
      }, RETRY_SSE_TIME)
    }
  }

  destroySse = () => {
    if (this.sse) {
      this.sse.close()
      this.sse = undefined
    }
  }

  getDecodeRequest = () => {
    const { parcel } = this.state
    const decodePayload = getDataFromSchema(this.state.formData, this.formSchema)
    decodePayload.operationId = this.operation.id
    decodePayload.parcelCode = decodePayload.parcelCode ?? parcel?.code
    if (!decodePayload.clientPlaceId) {
      decodePayload.clientPlaceId = AppStore.defaultWorkstation?.placeId
    }
    return {
      url: Shippings.batchValidateEndpoint(this.operation),
      payload: {
        ...decodePayload,
        asn: undefined,
        // asn: { ...decodePayload.asn, originPlaceId: AppStore.defaultWorkstation?.placeId }
      },
    }
  }

  setRfidReaderDecode = () => {
    RfidReader.setOnDecodedItemCallback(this.onDecodedItemCallback, this.getDecodeRequest())
  }

  updateParcel = async (action: 'INSERT' | 'UPDATE' | 'CLEAR' | 'REMOVE', itemIds: string[], parcelCode: string) => {
    const parcel = await Shippings.updateParcel(this.operation, {
      operationId: this.operation.id,
      itemIds,
      action,
      parcelCode,
    })
    return parcel
  }

  onParcelConfirmIsSuitable = async () => {
    if (this.state.autoConfirmParcel) {
      await this.antennaRef.current.stopReader()
      RfidReader.setAutomaticStop(false)
      RfidReader.stopTimer()
      const userSetAutostopAntennaTimeout: number =
        (await AppStore.getUserSetting('autostopAntennaTimeout', this.operation.id))?.value ||
        this.operation.autostopAntennaTimeout
      setTimeout(() => {
        this.setState({ confirming: true }, () => {
          this.onConfirm()
        })
      }, Math.max(userSetAutostopAntennaTimeout ?? 0, 1500) - 1500)
    }
  }

  decodedItemCheck = async (itemMapFromReader: { [tagCode: string]: DecodedItem }) => {
    return true
  }

  onDecodedItemCallback = async (itemMapFromReader: { [tagCode: string]: DecodedItem }) => {
    const { items, formData, removeModeEnabled, parcel } = this.state
    if (removeModeEnabled && this.isRemoveModeByRfidRead) {
      const itemsToRemove = Object.values(itemMapFromReader)
      itemsToRemove.forEach((item) => this.removeItem(item))
    } else {
      if (!(await this.decodedItemCheck(itemMapFromReader))) return
      const processedItems: DecodedItem[] = await OperationReadingProvider.processDecodedItems(
        this.operation,
        itemMapFromReader,
        items,
        formData,
        ShippingExtensions
      )
      if (this.isBatchReadingMode) {
        if (!this.state.parcel) return
        try {
          await this.updateParcel(
            'INSERT',
            processedItems
              .filter((itm) =>
                Object.values(itemMapFromReader)
                  .map((readerItem) => readerItem.item?.id)
                  .includes(itm.item?.id)
              )
              .filter(filterIgnoredAndErrorItems)
              .flatMap(({ item }) => (item?.id ? item.id : [])),
            this.state.parcel?.code
          )
        } catch (error) {
          showToastError(error, __(T.error.error), this.isModal)
        }
      }
      this.setState({ items: processedItems }, async () => {
        this.onItemsDecodedCallback()

        if (parcel) {
          if (this.parcelIsSuitableForConfirmation()) {
            this.onParcelConfirmIsSuitable()
          }
        }
      })
    }
  }

  onItemsDecodedCallback = () => {
    // extensible
  }

  async removeItem(decodedItem: DecodedItem) {
    const { parcel, items } = this.state
    if (this.isBatchReadingMode) {
      if (!parcel) return
      await this.updateParcel(
        'REMOVE',
        Object.values(decodedItem)
          .map((itm) => itm.item?.id)
          .filter(Boolean) as string[],
        parcel.code
      )
    }
    const newItems = OperationReadingProvider.removeItem(decodedItem, items)
    this.setState({ items: newItems })
  }

  initRemoveModeByRfid = () => {
    if (this.isRemoveModeByRfidRead) {
      RfidReader.clear()
    }
  }

  onConfirm = async () => {
    const { items } = this.state
    if (items.filter(filterErrorItems).length > 0) {
      showToastError(__(T.error.confirm_not_allowed_with_errors), __(T.error.error), true)
    } else {
      this.setState({ confirming: true }, this._confirm)
    }
  }

  getConfirmData = () => {
    const parcel = this.state.parcel ?? this.locationState.parcel
    const { formData } = this.state
    const confirmData = getDataFromSchema(formData, this.formSchema)
    if (this.asn && this.asn.code && (!confirmData.asn || !confirmData.asn.code)) {
      if (!confirmData.asn) confirmData.asn = {}
      confirmData.asn.code = this.asn.code
    } else if (parcel && parcel.asn && parcel.asn.code && (!confirmData.asn || !confirmData.asn.code)) {
      if (!confirmData.asn) confirmData.asn = {}
      confirmData.asn.code = parcel.asn.code
    }
    if (confirmData.asn) {
      if (this.operation.type === 'INBOUND' && !confirmData.asn?.destinationPlaceId) {
        confirmData.asn = {
          ...confirmData.asn,
          destinationPlaceId: AppStore.defaultWorkstation?.placeId,
        } as any
      } else if (this.operation.type === 'OUTBOUND' && !confirmData.asn?.originPlaceId) {
        confirmData.asn = {
          ...confirmData.asn,
          originPlaceId: AppStore.defaultWorkstation?.placeId,
        } as any
      } else if (this.operation.type === 'PACKING' && !confirmData.operationPlaceId) {
        confirmData.operationPlaceId = AppStore.defaultWorkstation!.placeId
      }
    }
    return confirmData
  }

  createParcel = async () => {
    let parcel = this.state.parcel ?? this.locationState.parcel
    const { items } = this.state
    const confirmData = this.getConfirmData()
    if (!parcel) {
      parcel = await this.startParcel(confirmData)
    }
    return { parcel, confirmData, items }
  }

  startParcel = async (confirmData) => {
    if (!AppStore.defaultWorkstation?.placeId) throw new Error('Missing workstation place')
    const payload = {
      operationId: this.operation.id,
      attributes: confirmData.attributes,
      ...confirmData,
      asn: { ...confirmData.asn },
    }
    try {
      const parcel = await Shippings.startParcel(this.operation, payload)
      return parcel
    } catch (err) {
      if (err instanceof CustomError) {
        showToastError(
          `${CustomError.interpolateMessage(err.message, payload)}. [${err.errorCode}]`,
          __(T.error.error),
          this.isModal
        )
        return
      }
      console.error(err)
    }
  }

  openRequiredFieldsModal = async (isShowingOnConfirm = false) => {
    try {
      await new Promise((resolve, reject) => {
        AppStore.callbacks.openModalForm(
          resolve,
          this._cancel,
          isShowingOnConfirm ? __UP(T.misc.update_and_confirm) : __UP(T.misc.confirm),
          __UP(T.misc.back)
        )
      })
    } catch (error) {
      this.setState({ confirming: false })
      return
    }
  }

  _cancel = () => {
    this.setState({ confirming: false })
  }

  _confirm = async () => {
    const asyncJobs = hasAsyncConfirm(this.operation)
    try {
      if (this.antennaRef && this.antennaRef.current) {
        await this.antennaRef.current.stopReader()
      }

      //check if some data of schema is required and undefined: if check is false then open Modal Form
      if (this.operation.showModalOnConfirm || !checkRequiredField(this.state.formData, this.formSchema)) {
        await this.openRequiredFieldsModal(true)
      }

      if (hasChecklist(this.operation) && this.state.parcel && this.state.parcel.checklist) {
        const { validationResult, message } = validateChecklistReadings(
          this.state.items,
          this.operation,
          ShippingExtensions,
          this.state.parcel.checklist,
          this.state.parcel.products
        )
        if (validationResult === 'KO') {
          showToastError(new Error(message), __(T.error.error), this.isModal)
          this.setState({ confirming: false })
          return
        }
        if (validationResult === 'WARNING') {
          const askConfirmResult = await askUserConfirmation(
            __(T.misc.checklist),
            message,
            __(T.misc.no),
            __(T.misc.yes)
          )
          if (!askConfirmResult) {
            this.setState({ confirming: false })
            return
          }
        }
      }

      const parcelResponse = await this.createParcel()
      let parcel: ShippingParcel | undefined = parcelResponse?.parcel
      const confirmData: Record<PropertyKey, any> = parcelResponse?.confirmData ?? {}
      const items = parcelResponse?.items ?? []

      if (!parcel) throw new Error('Missing parcel')

      await ShippingExtensions.beforeConfirm(this.operation, confirmData, items)

      if (!this.isBatchReadingMode) {
        await this.updateParcel('CLEAR', [], parcel.code)
        parcel = await this.updateParcel(
          'INSERT',
          items.filter(filterIgnoredAndErrorItems).flatMap(({ item }) => (item?.id ? item.id : [])),
          parcel.code
        )
      }

      const confirmParcelData: any = {
        operationId: this.operation.id,
        attributes: confirmData.attributes || {},
        ...confirmData,
        parcelCode: parcel.code,
      }
      const result: any = await Shippings.confirmParcel(
        this.operation,
        confirmParcelData,
        false,
        asyncJobs,
        confirmData.asn ? true : false
      )
      if (asyncJobs) {
        if (result && result.asyncExecutionId) {
          this.setState({ lastAsyncID: result.asyncExecutionId })
        } else {
          this._confirmFailed()
        }
      } else {
        this._confirmSuccess(result)
      }
    } catch (err) {
      this.setState({ confirming: false })
      if (err instanceof CustomError) {
        const payloadData = getDataFromSchema(this.state.formData, this.formSchema)
        showToastError(
          `${CustomError.interpolateMessage(err.message, payloadData)}. [${err.errorCode}]`,
          __(T.error.error),
          this.isModal
        )
        return
      }
      showToastError(err, __(T.error.error), this.isModal)
    }
  }

  _confirmFailed = (message?: string) => {
    showToastError(message && message != '' ? message : __(T.error.operation_confirm_error), __(T.error.error), true)
    this.setState({ confirming: false })
  }

  _confirmSuccess = async (result?) => {
    const confirmData = this.getConfirmData()
    await ShippingExtensions.afterConfirm(this.operation, confirmData, result || {})
    this.setState({ confirming: false })
    if (result?.status === 'READY_TO_RECEIVE' || result?.status === 'RECEIVED') {
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.parcel_success),
        status: 'success',
      })
    } else {
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.generic_success, { code: this.operation.description }),
        status: 'success',
      })
    }

    this.afterConfirmSuccess()
  }

  afterConfirmSuccess = async () => {
    if (['hideSuccessModal', 'disabled'].includes(this.operation.postConfirmAction)) {
      this.goBack()
    } else {
      if (
        await askUserConfirmation(
          __(T.confirm.post_confirm_action_title),
          this.operation.postConfirmAction === 'keepInput'
            ? __(T.confirm.post_confirm_action_keep_input)
            : __(T.confirm.post_confirm_action_change_input),
          __(T.misc.no),
          __(T.misc.yes)
        )
      ) {
        if (this.operation.postConfirmAction === 'keepInput') {
          this.onClearKeepInput()
        } else {
          this.goBack()
        }
      } else {
        this.goDashboard()
      }
    }
  }

  goDashboard = () => {
    Router.navigate('/')
  }

  goBack = () => {
    if (this.locationState?.backRoute) {
      Router.navigate(this.locationState?.backRoute, this.locationState?.backRouteParams ?? {})
    } else if (this.formSchema.length) {
      Router.navigate('/shipping/:opCode', { opCode: this.operation.code })
    } else {
      this.goDashboard()
    }
  }

  onClear = async () => {
    const { parcel } = this.state
    if (this.isBatchReadingMode && parcel) {
      await this.updateParcel('CLEAR', [], parcel.code)
    }
    this.setState({ items: [] })
    RfidReader.clear()
  }

  onClearKeepInput = async () => {
    if (this.isBatchReadingMode) {
      const confirmData = this.getConfirmData()
      const parcel = await this.startParcel(confirmData)
      this.setState({ items: [], parcel: parcel })
    } else {
      this.setState({ items: [], parcel: undefined })
    }
    RfidReader.clear()
  }

  showConfirmButton() {
    const { confirming, autoConfirmParcel } = this.state

    if (this.canShowConfirmButton()) {
      const enabled = this.confirmButtonEnabled()
      return (
        <Button
          style={{ marginTop: 15 }}
          variant={autoConfirmParcel ? 'plain' : enabled ? 'primary' : 'ignore'}
          loading={confirming}
          disabled={!enabled}
          title={!autoConfirmParcel ? __(T.misc.confirm) : __(T.misc.confirm_auto)}
          onClick={this.onConfirm}
        />
      )
    }
    return null
  }

  setFormData = async (fd) => {
    if (!(await ShippingExtensions.formDataIsValid(fd, this.operation, this.formSchema))) return
    this.setState({ formData: fd })
    this.setRfidReaderDecode()
  }

  onRemoveChange: (checked: boolean) => void = (removeModeEnabled) => {
    this.setState({ removeModeEnabled }, this.initRemoveModeByRfid)
  }

  handleOnUserSettingChanged(settingName: string, newValue: string | number | boolean) {
    switch (settingName) {
      case 'autostartAntenna':
        this.setState({ autostartAntenna: newValue as boolean })
        break
      case 'autoConfirmParcel':
        this.setState({ autoConfirmParcel: newValue as boolean })
        break
      case 'autostopAntennaTimeout':
        RfidReader.setAutomaticStop(Number(newValue) > 0)
        RfidReader.setAutomaticStopTime(Number(newValue))
        break
      case 'barcodeItemRead':
        this.setState({ barcodeItemReading: newValue as boolean })
        break
      default:
        console.warn('Unhandled setting change', settingName)
        break
    }
  }

  upperSideBarComponent: () => JSX.Element = () => {
    return <></>
  }

  antennaStartButtonEnabled = () => {
    return true
  }

  antennaClearButtonVisible = () => {
    return this.state.items.length !== 0
  }

  overrideProductInfoFields: () => ProductInfoField[] | undefined = () => {
    return undefined
  }

  getHideIdentifierZones: () => boolean = () => {
    return false
  }

  getHideIdentifierCodeByType = (): IdentifierType[] | undefined => {
    return undefined
  }

  getDisableModalRowCounters = () => {
    return false
  }

  getBarcodeModeOptions: () => BarcodeModeOptions | undefined = () => {
    return undefined
  }

  render() {
    const { items, formData, loading, parcel, sseStatus, removeModeEnabled, initReaderTags, barcodeItemReading } =
      this.state

    let tagCounterExpecteds = 0
    let tagCounterUnexpecteds = 0
    if (parcel?.checklist) {
      const { expecteds, unexpecteds, overQty } = getChecklistItemsCount(
        items,
        this.operation,
        ShippingExtensions,
        parcel.checklist,
        parcel.products
      )
      tagCounterExpecteds = expecteds
      tagCounterUnexpecteds = unexpecteds + overQty
    }
    return (
      <Page
        title={this.operation.description}
        onBackPress={() => this.goBack()}
        alertOnBackPress={
          this.operation.showAlertOnExit &&
          !this.isBatchReadingMode &&
          items.filter(filterIgnoredAndErrorItems).length > 0
        }
        loading={loading}
        headerRight={
          <>
            {hasAsyncConfirm(this.operation) ? <Stoplight active={sseStatus} /> : undefined}
            {this.operationRemoveModeAllowed && <RemoveMode onRemoveChange={this.onRemoveChange} />}
          </>
        }
        header={{
          details: {
            data: _cloneDeep(formData),
            formSchema: this.formSchema,
            asn: this.asn,
            setFormData: this.setFormData,
            resetFormData: async (fd) => {
              this.setState({ formData: fd })
            },
            operationId: this.operation.id,
          },
        }}
        userSettings={{ operation: this.operation, settings: this.operation.userSettings }}
        onUserSettingChanged={(name, newValue) => this.handleOnUserSettingChanged(name, newValue)}
        enableEmulation
      >
        <Page.Sidebar>
          <Box flex style={{ overflowY: 'auto' }}>
            {this.upperSideBarComponent()}
            <TagCounter
              detected={items
                .filter(filterIgnoredItems)
                .reduce(
                  (acc, item) => acc + OperationReadingProvider.getItemIntrinsicQuantity(item, ShippingExtensions),
                  0
                )}
              expected={tagCounterExpecteds}
              unexpected={tagCounterUnexpecteds}
            />
            <AntennaButton
              ref={this.antennaRef}
              mode={barcodeItemReading ? 'barcode' : 'antenna'}
              decodeRequest={this.getDecodeRequest()}
              onItemDecoded={this.onDecodedItemCallback}
              onClear={this.onClear}
              onStart={async () => {
                const userSetAutostopAntennaTimeout: number =
                  (await AppStore.getUserSetting('autostopAntennaTimeout', this.operation.id))?.value ||
                  this.operation.autostopAntennaTimeout
                RfidReader.setAutomaticStop(userSetAutostopAntennaTimeout > 0)
                RfidReader.setAutomaticStopTime(userSetAutostopAntennaTimeout)
              }}
              hideClear={!this.antennaClearButtonVisible()}
              initReaderTags={initReaderTags}
              disabled={!this.antennaStartButtonEnabled()}
              barcodeModeOptions={this.getBarcodeModeOptions()}
            />
            {checkItemsCounterError(items) && (
              <>
                <Spacer />
                <OperationReadingCounters
                  operation={this.operation}
                  items={items}
                  hideIdentifiersByType={this.getHideIdentifierCodeByType()}
                  hideIdentifiersZones={this.getHideIdentifierZones()}
                  overrideProductInfoFields={this.overrideProductInfoFields()}
                  disableModalRowCounters={this.getDisableModalRowCounters()}
                />
              </>
            )}
          </Box>
          {this.showConfirmButton()}
        </Page.Sidebar>
        <Page.Content notBoxed>
          <OperationReadingList
            removeItemCallback={this.removeItem}
            extension={ShippingExtensions}
            items={items}
            operation={this.operation}
            checklist={parcel ? parcel.checklist : undefined}
            products={parcel ? parcel.products : undefined}
            overrideProductInfoFields={this.overrideProductInfoFields()}
            hideIdentifiersZones={this.getHideIdentifierZones()}
            hideIdentifiersByType={this.getHideIdentifierCodeByType()}
            removeModeEnabled={removeModeEnabled && this.isRemoveModeBySku}
          />
        </Page.Content>
        {!sseStatus && (
          <FullLoadingLayer
            message={!sseStatus ? __(T.messages.connection_in_progress) : __(T.messages.operation_in_progress)}
          />
        )}
      </Page>
    )
  }
}
